import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { FinancialAccountSelectorComponent } from '../../shared/design-system/financial-account-selector.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinAttachmentsPanelComponent } from '../../shared/design-system/attachments-panel.component';
import { AinAttachmentItem } from '../../shared/design-system/attachment.model';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  AccountingCustomerRecord,
  AccountingWarehouseRecord,
  BankRecord,
  CashboxRecord,
  CreatePaymentRecordPayload,
  FinanceApiService,
  FinancialAccountRecord,
  PaymentPersonalChequeDetailRecord,
  PaymentRecord,
  ReceiptChequeDetailRecord,
  ReceiptCashDetailRecord,
  ReceiptAttachmentRecord,
  ReceiptPosDeviceDetailRecord,
  ReceiptTransferVoucherDetailRecord,
  UpdatePaymentRecordPayload
} from './finance-api.service';

type PayoutTab = 'cash' | 'cheque' | 'personalCheque' | 'pos' | 'transfer' | 'documents' | 'attachments';
type FloatingEntityType = 'None' | 'Person' | 'OrganizationUnit' | 'Bank' | 'CashFund' | 'Warehouse';

interface FloatingEntityOption {
  key: string;
  label: string;
}

interface BankOption {
  id: string;
  label: string;
}

interface CashboxOption {
  id: string;
  label: string;
}

@Component({
  standalone: true,
  selector: 'ain-finance-payout-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    RouterLink,
    FormsModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    TableModule,
    JalaliDateInputComponent,
    CurrencySelectorComponent,
    CustomerSelectorComponent,
    FinancialAccountSelectorComponent,
    AinAttachmentsPanelComponent
  ],
  template: `
    <ain-page
      [title]="isCreateMode() ? t('finance.payouts.form.createTitle') : t('finance.payouts.form.editTitle')"
      [description]="t('finance.payouts.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/financial/payments/payouts']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
        </div>

        <section class="card">
          <h3>{{ t('finance.payouts.sections.info') }}</h3>
          <div class="grid">
            <label>
              <span>{{ t('finance.payouts.fields.number') }}</span>
              <input
                pInputText
                type="text"
                [ngModel]="form.number"
                [disabled]="true"
                [placeholder]="t('finance.payouts.fields.numberAuto')" />
            </label>
            <label>
              <span>{{ t('finance.payouts.fields.paymentDate') }}</span>
              <ain-date-input [(ngModel)]="form.paymentDateUtc" />
            </label>
            <label class="wide">
              <span>{{ t('finance.payouts.fields.accountingAccountId') }}</span>
              <ain-financial-account-selector
                [(ngModel)]="form.accountingAccountId"
                (accountSelected)="onAccountSelected($event)" />
            </label>
            @if (floatingEnabled()) {
              <label class="wide">
                <span>{{ floatingLabel() }}</span>
                @if (headerFloatingType() === 'Person') {
                  <ain-customer-selector [(ngModel)]="form.floatingEntityId" />
                } @else {
                  <p-select
                    class="field-select"
                    [options]="headerFloatingOptions()"
                    optionLabel="label"
                    optionValue="key"
                    [placeholder]="t('finance.payouts.fields.selectFloatingEntity')"
                    [(ngModel)]="form.floatingEntityId"
                    appendTo="body" />
                }
              </label>
            }
            <label class="wide">
              <span>{{ t('finance.payouts.fields.description') }}</span>
              <input pInputText type="text" [(ngModel)]="form.description" />
            </label>
            <label>
              <span>{{ t('finance.payouts.fields.totalAmount') }}</span>
              <input pInputText type="text" [ngModel]="totalAmount()" disabled />
            </label>
            <label>
              <span>{{ t('columns.currency') }}</span>
              <ain-currency-selector
                [showSearch]="true"
                [ngModel]="form.currencyCode"
                (ngModelChange)="form.currencyCode = $event ?? 'IRR'" />
            </label>
          </div>
        </section>

        <section class="card">
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'cash'" (click)="activeTab.set('cash')">{{ t('finance.payouts.tabs.cash') }}</button>
            <button type="button" [class.active]="activeTab() === 'cheque'" (click)="activeTab.set('cheque')">{{ t('finance.payouts.tabs.cheque') }}</button>
            <button type="button" [class.active]="activeTab() === 'personalCheque'" (click)="activeTab.set('personalCheque')">{{ t('finance.payouts.tabs.personalCheque') }}</button>
            <button type="button" [class.active]="activeTab() === 'pos'" (click)="activeTab.set('pos')">{{ t('finance.payouts.tabs.pos') }}</button>
            <button type="button" [class.active]="activeTab() === 'transfer'" (click)="activeTab.set('transfer')">{{ t('finance.payouts.tabs.transfer') }}</button>
            <button type="button" [class.active]="activeTab() === 'documents'" (click)="activeTab.set('documents')">{{ t('finance.payouts.tabs.documents') }}</button>
            <button type="button" [class.active]="activeTab() === 'attachments'" (click)="activeTab.set('attachments')">{{ t('finance.payouts.tabs.attachments') }}</button>
          </div>

          @if (activeTab() === 'cash') {
            <div class="lines-header">
              <h4>{{ t('finance.payouts.tabs.cash') }}</h4>
              <button pButton type="button" [label]="t('common.add')" (click)="addCashDetail()"></button>
            </div>
            <p-table [value]="cashDetails()" styleClass="lines-table">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('finance.payouts.cash.fields.cashboxId') }}</th>
                  <th>{{ t('finance.payouts.cash.fields.amount') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row let-i="rowIndex">
                <tr>
                  <td>
                    <p-select
                      class="field-select"
                      [options]="cashboxOptions()"
                      optionLabel="label"
                      optionValue="id"
                      [placeholder]="t('finance.payouts.cash.selectCashbox')"
                      [(ngModel)]="row.cashboxId"
                      appendTo="body" />
                  </td>
                  <td><p-inputNumber [(ngModel)]="row.amount" (ngModelChange)="touchDetails()" [min]="0" [maxFractionDigits]="2" [useGrouping]="true" /></td>
                  <td><button pButton type="button" [label]="t('common.delete')" severity="danger" text="true" (click)="removeCashDetail(i)"></button></td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (activeTab() === 'cheque') {
            <div class="lines-header">
              <h4>{{ t('finance.payouts.tabs.cheque') }}</h4>
              <button pButton type="button" [label]="t('common.add')" (click)="addChequeDetail()"></button>
            </div>
            <p-table [value]="chequeDetails()" styleClass="lines-table">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('finance.payouts.cheque.fields.bankId') }}</th>
                  <th>{{ t('finance.payouts.cheque.fields.branchCode') }}</th>
                  <th>{{ t('finance.payouts.cheque.fields.chequeNumber') }}</th>
                  <th>{{ t('finance.payouts.cheque.fields.siadNumber') }}</th>
                  <th>{{ t('finance.payouts.cheque.fields.payee') }}</th>
                  <th>{{ t('finance.payouts.cheque.fields.holderNationalCode') }}</th>
                  <th>{{ t('finance.payouts.cheque.fields.chequeAmount') }}</th>
                  <th>{{ t('finance.payouts.cheque.fields.dueDate') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row let-i="rowIndex">
                <tr>
                  <td>
                    <p-select class="field-select" [options]="bankOptions()" optionLabel="label" optionValue="id" [(ngModel)]="row.bankId" appendTo="body" />
                  </td>
                  <td><input pInputText type="text" [(ngModel)]="row.branchCode" /></td>
                  <td><input pInputText type="text" [(ngModel)]="row.chequeNumber" /></td>
                  <td><input pInputText type="text" [(ngModel)]="row.siadNumber" /></td>
                  <td><input pInputText type="text" [(ngModel)]="row.payee" /></td>
                  <td><input pInputText type="text" [(ngModel)]="row.holderNationalCode" /></td>
                  <td><p-inputNumber [(ngModel)]="row.chequeAmount" (ngModelChange)="touchDetails()" [min]="0" [maxFractionDigits]="2" [useGrouping]="true" /></td>
                  <td><ain-date-input [(ngModel)]="row.dueDateUtc" /></td>
                  <td><button pButton type="button" [label]="t('common.delete')" severity="danger" text="true" (click)="removeChequeDetail(i)"></button></td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (activeTab() === 'personalCheque') {
            <div class="lines-header">
              <h4>{{ t('finance.payouts.tabs.personalCheque') }}</h4>
              <button pButton type="button" [label]="t('common.add')" (click)="addPersonalChequeDetail()"></button>
            </div>
            <p-table [value]="personalChequeDetails()" styleClass="lines-table">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('finance.payouts.cheque.fields.bankId') }}</th>
                  <th>{{ t('finance.payouts.cheque.fields.chequeNumber') }}</th>
                  <th>{{ t('finance.payouts.cheque.fields.siadNumber') }}</th>
                  <th>{{ t('finance.payouts.cheque.fields.payee') }}</th>
                  <th>{{ t('finance.payouts.cheque.fields.holderNationalCode') }}</th>
                  <th>{{ t('finance.payouts.cheque.fields.chequeAmount') }}</th>
                  <th>{{ t('finance.payouts.cheque.fields.dueDate') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row let-i="rowIndex">
                <tr>
                  <td><p-select class="field-select" [options]="bankOptions()" optionLabel="label" optionValue="id" [(ngModel)]="row.bankId" appendTo="body" /></td>
                  <td><input pInputText type="text" [(ngModel)]="row.chequeNumber" /></td>
                  <td><input pInputText type="text" [(ngModel)]="row.siadNumber" /></td>
                  <td><input pInputText type="text" [(ngModel)]="row.payee" /></td>
                  <td><input pInputText type="text" [(ngModel)]="row.holderNationalCode" /></td>
                  <td><p-inputNumber [(ngModel)]="row.chequeAmount" (ngModelChange)="touchDetails()" [min]="0" [maxFractionDigits]="2" [useGrouping]="true" /></td>
                  <td><ain-date-input [(ngModel)]="row.dueDateUtc" /></td>
                  <td><button pButton type="button" [label]="t('common.delete')" severity="danger" text="true" (click)="removePersonalChequeDetail(i)"></button></td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (activeTab() === 'pos') {
            <div class="lines-header">
              <h4>{{ t('finance.payouts.tabs.pos') }}</h4>
              <button pButton type="button" [label]="t('common.add')" (click)="addPosDetail()"></button>
            </div>
            <p-table [value]="posDetails()" styleClass="lines-table">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('finance.payouts.pos.fields.bankId') }}</th>
                  <th>{{ t('finance.payouts.pos.fields.deviceSerialNumber') }}</th>
                  <th>{{ t('finance.payouts.pos.fields.amount') }}</th>
                  <th>{{ t('finance.payouts.pos.fields.posAccountingAccountId') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row let-i="rowIndex">
                <tr>
                  <td><p-select class="field-select" [options]="bankOptions()" optionLabel="label" optionValue="id" [(ngModel)]="row.bankId" appendTo="body" /></td>
                  <td><input pInputText type="text" [(ngModel)]="row.deviceSerialNumber" /></td>
                  <td><p-inputNumber [(ngModel)]="row.amount" (ngModelChange)="touchDetails()" [min]="0" [maxFractionDigits]="2" [useGrouping]="true" /></td>
                  <td><ain-financial-account-selector [(ngModel)]="row.posAccountingAccountId" /></td>
                  <td><button pButton type="button" [label]="t('common.delete')" severity="danger" text="true" (click)="removePosDetail(i)"></button></td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (activeTab() === 'transfer') {
            <div class="lines-header">
              <h4>{{ t('finance.payouts.tabs.transfer') }}</h4>
              <button pButton type="button" [label]="t('common.add')" (click)="addTransferDetail()"></button>
            </div>
            <p-table [value]="transferDetails()" styleClass="lines-table">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('finance.payouts.transfer.fields.bankId') }}</th>
                  <th>{{ t('finance.payouts.transfer.fields.transferredAmount') }}</th>
                  <th>{{ t('finance.payouts.transfer.fields.transactionNumber') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row let-i="rowIndex">
                <tr>
                  <td><p-select class="field-select" [options]="bankOptions()" optionLabel="label" optionValue="id" [(ngModel)]="row.bankId" appendTo="body" /></td>
                  <td><p-inputNumber [(ngModel)]="row.transferredAmount" (ngModelChange)="touchDetails()" [min]="0" [maxFractionDigits]="2" [useGrouping]="true" /></td>
                  <td><input pInputText type="text" [(ngModel)]="row.transactionNumber" /></td>
                  <td><button pButton type="button" [label]="t('common.delete')" severity="danger" text="true" (click)="removeTransferDetail(i)"></button></td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (activeTab() === 'documents') {
            <div class="documents">
              @if (journalEntryId()) {
                <a class="document-link" [href]="journalEntryUrl()" target="_blank" rel="noopener noreferrer">
                  {{ t('finance.payouts.documents.journalEntry') }}: {{ journalNumber() || journalEntryId() }}
                </a>
              } @else if (postingErrorCode()) {
                <p class="message error">{{ t('finance.payouts.documents.postingError') }}: {{ postingErrorCode() }}</p>
              } @else {
                <p class="muted">{{ t('finance.payouts.documents.empty') }}</p>
              }
            </div>
          }

          @if (activeTab() === 'attachments') {
            <ain-attachments-panel
              [disabled]="isCreateMode()"
              [disabledMessage]="t('finance.payouts.attachments.saveFirst')"
              [items]="attachmentItems()"
              [uploading]="uploadingAttachment()"
              [emptyMessage]="t('finance.payouts.attachments.empty')"
              [uploadLabel]="t('finance.payouts.attachments.upload')"
              (upload)="onAttachmentSelected($event)"
              (deleteItem)="deleteAttachment($event)" />
          }
        </section>

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .card h3, .card h4 { margin: 0; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .wide { grid-column: span 3; }
    label { display: grid; gap: 6px; }
    .field-select { width: 100%; }
    .tabs { display: flex; flex-wrap: wrap; gap: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; }
    .tabs button { border: 0; background: transparent; padding: 6px 10px; border-radius: 8px; cursor: pointer; color: #334155; }
    .tabs button.active { background: #dbeafe; color: #1e40af; font-weight: 600; }
    .lines-header { display: flex; justify-content: space-between; align-items: center; }
    .documents { display: grid; gap: 8px; }
    .document-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .message.error { color: #b91c1c; }
    .muted { color: #64748b; margin: 0; }
    @media (max-width: 1200px) {
      .grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .wide { grid-column: span 2; }
    }
    @media (max-width: 760px) {
      .grid { grid-template-columns: 1fr; }
      .wide { grid-column: span 1; }
    }
  `]
})
export class FinancePayoutDetailComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly errorMessage = signal('');
  readonly isCreateMode = signal(false);
  readonly activeTab = signal<PayoutTab>('cash');
  readonly accounts = signal<FinancialAccountRecord[]>([]);
  readonly bankOptions = signal<BankOption[]>([]);
  readonly cashboxOptions = signal<CashboxOption[]>([]);
  readonly floatingCustomers = signal<AccountingCustomerRecord[]>([]);
  readonly floatingBanks = signal<BankRecord[]>([]);
  readonly floatingCashboxes = signal<CashboxRecord[]>([]);
  readonly floatingWarehouses = signal<AccountingWarehouseRecord[]>([]);
  readonly headerFloatingType = signal<FloatingEntityType>('None');
  readonly cashDetails = signal<ReceiptCashDetailRecord[]>([]);
  readonly chequeDetails = signal<ReceiptChequeDetailRecord[]>([]);
  readonly posDetails = signal<ReceiptPosDeviceDetailRecord[]>([]);
  readonly transferDetails = signal<ReceiptTransferVoucherDetailRecord[]>([]);
  readonly personalChequeDetails = signal<PaymentPersonalChequeDetailRecord[]>([]);
  readonly attachments = signal<ReceiptAttachmentRecord[]>([]);
  readonly journalEntryId = signal('');
  readonly journalNumber = signal('');
  readonly postingErrorCode = signal('');
  readonly uploadingAttachment = signal(false);
  private readonly detailsRevision = signal(0);

  readonly floatingEnabled = computed(() => this.headerFloatingType() !== 'None');
  readonly headerFloatingOptions = computed(() => this.floatingOptionsByType(this.headerFloatingType()));
  readonly journalEntryUrl = computed(() => {
    const journalId = this.journalEntryId().trim();
    return journalId ? `/financial/management/journal-entry/${journalId}/edit` : '';
  });
  readonly attachmentItems = computed<AinAttachmentItem[]>(() =>
    this.attachments().map(item => ({
      id: item.id,
      fileName: item.fileName,
      contentType: item.contentType,
      fileSizeBytes: item.fileSizeBytes,
      downloadUrl: item.downloadUrl
    })));
  readonly totalAmount = computed(() => {
    this.detailsRevision();
    const cash = this.cashDetails().reduce((sum, item) => sum + (item.amount || 0), 0);
    const cheque = this.chequeDetails().reduce((sum, item) => sum + (item.chequeAmount || 0), 0);
    const pos = this.posDetails().reduce((sum, item) => sum + (item.amount || 0), 0);
    const transfer = this.transferDetails().reduce((sum, item) => sum + (item.transferredAmount || 0), 0);
    const personalCheque = this.personalChequeDetails().reduce((sum, item) => sum + (item.chequeAmount || 0), 0);
    return cash + cheque + personalCheque + pos + transfer;
  });

  readonly form = {
    number: '',
    paymentDateUtc: new Date().toISOString().slice(0, 10),
    accountingAccountId: '',
    currencyCode: 'IRR',
    floatingEntityType: 'None',
    floatingEntityId: '',
    description: ''
  };

  private paymentRecordId = '';
  private isAutoNumber = true;

  ngOnInit(): void {
    this.loadReferenceData();
    const paymentRecordId = this.route.snapshot.paramMap.get('paymentRecordId');
    if (!paymentRecordId || paymentRecordId === 'new') {
      this.isCreateMode.set(true);
      this.loadNumberPreview();
      this.loading.set(false);
      return;
    }

    this.paymentRecordId = paymentRecordId;
    this.api.getAccountingPaymentRecord(paymentRecordId).subscribe({
      next: row => {
        this.patchForm(row);
        this.loading.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('finance.payouts.messages.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  save(): void {
    this.errorMessage.set('');
    if (this.totalAmount() <= 0) {
      this.errorMessage.set(this.t('finance.payouts.messages.invalidAmount'));
      return;
    }
    if (this.floatingEnabled() && !this.form.floatingEntityId.trim()) {
      this.errorMessage.set(this.t('finance.payouts.messages.floatingRequired'));
      return;
    }

    this.saving.set(true);
    const payloadBase = {
      amount: this.totalAmount(),
      currencyCode: this.form.currencyCode.trim() || 'IRR',
      paymentDateUtc: this.form.paymentDateUtc,
      accountingAccountId: this.form.accountingAccountId.trim(),
      floatingEntityType: this.headerFloatingType(),
      floatingEntityId: this.form.floatingEntityId.trim(),
      description: this.form.description.trim(),
      cashDetails: this.cashDetails()
        .filter(item => item.cashboxId.trim() && item.amount > 0)
        .map(item => ({
          cashboxId: item.cashboxId,
          amount: item.amount,
          currencyCode: this.form.currencyCode.trim() || 'IRR'
        })),
      chequeDetails: this.chequeDetails().filter(item => item.bankId.trim() && item.chequeAmount > 0),
      posDeviceDetails: this.posDetails().filter(item => item.bankId.trim() && item.amount > 0),
      transferVoucherDetails: this.transferDetails().filter(item => item.bankId.trim() && item.transferredAmount > 0),
      personalChequeDetails: this.personalChequeDetails().filter(item => item.bankId.trim() && item.chequeAmount > 0)
    };

    if (this.isCreateMode()) {
      const payload: CreatePaymentRecordPayload = {
        ...payloadBase,
        idempotencyKey: this.createIdempotencyKey()
      };
      this.api.createAccountingPaymentRecord(payload).subscribe({
        next: created => {
          this.saving.set(false);
          void this.router.navigate(['/financial/payments/payouts', created.id]);
        },
        error: () => {
          this.errorMessage.set(this.t('finance.payouts.messages.saveFailed'));
          this.saving.set(false);
        }
      });
      return;
    }

    const payload: UpdatePaymentRecordPayload = payloadBase;
    this.api.updateAccountingPaymentRecord(this.paymentRecordId, payload).subscribe({
      next: updated => {
        this.patchForm(updated);
        this.saving.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('finance.payouts.messages.saveFailed'));
        this.saving.set(false);
      }
    });
  }

  onAccountSelected(account: FinancialAccountRecord | null): void {
    const floatingType = this.normalizeFloatingEntityType(account?.floatingEntityType);
    this.headerFloatingType.set(floatingType);
    this.form.floatingEntityType = floatingType;
    if (floatingType === 'None') {
      this.form.floatingEntityId = '';
    }
  }

  floatingLabel(): string {
    switch (this.headerFloatingType()) {
      case 'Person':
        return this.t('finance.payouts.fields.customerId');
      case 'Bank':
        return this.t('finance.payouts.fields.bankId');
      case 'CashFund':
        return this.t('finance.payouts.fields.cashboxId');
      case 'Warehouse':
        return this.t('finance.payouts.fields.warehouseId');
      default:
        return this.t('finance.payouts.fields.floatingEntity');
    }
  }

  addCashDetail(): void {
    this.cashDetails.update(items => [...items, { cashboxId: '', amount: 0 }]);
    this.touchDetails();
  }

  removeCashDetail(index: number): void {
    this.cashDetails.update(items => items.filter((_, i) => i !== index));
    this.touchDetails();
  }

  addChequeDetail(): void {
    this.chequeDetails.update(items => [...items, {
      bankId: '',
      branchCode: '',
      chequeNumber: '',
      siadNumber: '',
      payee: '',
      holderNationalCode: '',
      chequeAmount: 0,
      dueDateUtc: new Date().toISOString().slice(0, 10)
    }]);
    this.touchDetails();
  }

  removeChequeDetail(index: number): void {
    this.chequeDetails.update(items => items.filter((_, i) => i !== index));
    this.touchDetails();
  }

  addPersonalChequeDetail(): void {
    this.personalChequeDetails.update(items => [...items, {
      bankId: '',
      chequeNumber: '',
      siadNumber: '',
      payee: '',
      holderNationalCode: '',
      chequeAmount: 0,
      dueDateUtc: new Date().toISOString().slice(0, 10)
    }]);
  }

  removePersonalChequeDetail(index: number): void {
    this.personalChequeDetails.update(items => items.filter((_, i) => i !== index));
    this.touchDetails();
  }

  addPosDetail(): void {
    this.posDetails.update(items => [...items, { bankId: '', deviceSerialNumber: '', amount: 0, posAccountingAccountId: '' }]);
    this.touchDetails();
  }

  removePosDetail(index: number): void {
    this.posDetails.update(items => items.filter((_, i) => i !== index));
    this.touchDetails();
  }

  addTransferDetail(): void {
    this.transferDetails.update(items => [...items, { bankId: '', transferredAmount: 0, transactionNumber: '' }]);
    this.touchDetails();
  }

  removeTransferDetail(index: number): void {
    this.transferDetails.update(items => items.filter((_, i) => i !== index));
    this.touchDetails();
  }

  touchDetails(): void {
    this.detailsRevision.update(value => value + 1);
  }

  onNumberChanged(value: string): void {
    this.form.number = value;
    this.isAutoNumber = false;
  }

  onAttachmentSelected(file: File): void {
    if (!this.paymentRecordId) {
      return;
    }

    this.uploadingAttachment.set(true);
    this.api.uploadAccountingPaymentRecordAttachment(this.paymentRecordId, file).subscribe({
      next: attachment => {
        this.attachments.update(items => [attachment, ...items]);
        this.uploadingAttachment.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('finance.payouts.attachments.uploadFailed'));
        this.uploadingAttachment.set(false);
      }
    });
  }

  deleteAttachment(attachmentId: string): void {
    if (!this.paymentRecordId) {
      return;
    }

    this.api.deleteAccountingPaymentRecordAttachment(this.paymentRecordId, attachmentId).subscribe({
      next: () => {
        this.attachments.update(items => items.filter(item => item.id !== attachmentId));
      },
      error: () => {
        this.errorMessage.set(this.t('finance.payouts.attachments.deleteFailed'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private patchForm(row: PaymentRecord): void {
    this.form.number = row.number;
    this.form.paymentDateUtc = row.paymentDateUtc?.slice(0, 10) || new Date().toISOString().slice(0, 10);
    this.form.accountingAccountId = row.accountingAccountId;
    this.form.currencyCode = row.currencyCode?.trim() || 'IRR';
    this.form.floatingEntityType = row.floatingEntityType || 'None';
    this.form.floatingEntityId = row.floatingEntityId;
    this.form.description = row.description;
    this.headerFloatingType.set(this.normalizeFloatingEntityType(row.floatingEntityType));
    this.cashDetails.set(row.cashDetails ?? []);
    this.chequeDetails.set(row.chequeDetails ?? []);
    this.posDetails.set(row.posDeviceDetails ?? []);
    this.transferDetails.set(row.transferVoucherDetails ?? []);
    this.personalChequeDetails.set(row.personalChequeDetails ?? []);
    this.attachments.set(row.attachments ?? []);
    this.journalEntryId.set(row.journalEntryId ?? '');
    this.journalNumber.set(row.journalNumber ?? '');
    this.postingErrorCode.set(row.postingErrorCode ?? '');
    this.isAutoNumber = false;
    this.touchDetails();
  }

  private loadNumberPreview(): void {
    this.api.previewNextRunningNumber('PaymentRecord', 'PaymentsAccounting', 'paymentrecord.issued').subscribe({
      next: preview => {
        if (preview && this.isAutoNumber) {
          this.form.number = preview;
        }
      }
    });
  }

  private loadReferenceData(): void {
    forkJoin({
      accounts: this.api.listFinancialAccounts().pipe(catchError(() => of([]))),
      banks: this.api.listAccountingBanks().pipe(catchError(() => of([]))),
      cashboxes: this.api.listAccountingCashboxes().pipe(catchError(() => of([]))),
      customers: this.api.listAccountingCustomers().pipe(catchError(() => of([]))),
      warehouses: this.api.listInventoryWarehouses().pipe(catchError(() => of([])))
    }).subscribe({
      next: ({ accounts, banks, cashboxes, customers, warehouses }) => {
        this.accounts.set(accounts);
        this.floatingCustomers.set(customers);
        this.floatingBanks.set(banks);
        this.floatingCashboxes.set(cashboxes);
        this.floatingWarehouses.set(warehouses);
        this.bankOptions.set(banks.map(item => ({
          id: item.id,
          label: item.accountNumber ? `${item.bankName} (${item.accountNumber})` : item.bankName
        })));
        this.cashboxOptions.set(cashboxes.map(item => ({
          id: item.id,
          label: item.name || item.number
        })));
      }
    });
  }

  private floatingOptionsByType(type: FloatingEntityType): FloatingEntityOption[] {
    switch (type) {
      case 'Person':
        return this.floatingCustomers().map(item => ({
          key: item.key,
          label: item.code ? `${item.displayName} (${item.code})` : item.displayName
        }));
      case 'Bank':
        return this.floatingBanks().map(item => ({
          key: item.id,
          label: item.accountNumber ? `${item.bankName} (${item.accountNumber})` : item.bankName
        }));
      case 'CashFund':
        return this.floatingCashboxes().map(item => ({
          key: item.id,
          label: item.name || item.number
        }));
      case 'Warehouse':
        return this.floatingWarehouses().map(item => ({
          key: item.id,
          label: item.code ? `${item.code} - ${item.name}` : item.name
        }));
      default:
        return [];
    }
  }

  private normalizeFloatingEntityType(value?: string): FloatingEntityType {
    switch (value) {
      case 'Person':
      case 'OrganizationUnit':
      case 'Bank':
      case 'CashFund':
      case 'Warehouse':
        return value;
      default:
        return 'None';
    }
  }

  private createIdempotencyKey(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID().replace(/-/g, '');
    }

    return `${Date.now()}${Math.floor(Math.random() * 1000000)}`;
  }
}
